/**
 * Editor parametrů ve stylu herních nastavení:
 *  - vlevo vertikální navigace sekcí (top-level kategorie),
 *  - vpravo obsah vybrané sekce,
 *  - v KAŽDÉ sekci/podsekci se složitější nastavení postupně rozklikává
 *    („Zobrazit pokročilé…" → „Zobrazit expertní…" → „Zobrazit vývojářské…"),
 *    nezávisle na ostatních sekcích. Žádný globální přepínač úrovní.
 *  - rerender zachovává pozici scrollu (žádné skákání nahoru).
 *
 * Používá ho globální Nastavení i editor profilu (přepisy parametrů).
 */
import {
  buildCategoryTree,
  PARAM_DEFS,
  PARAM_LEVEL_LABELS,
  PARAM_LEVELS,
  type ParamCategoryNode,
  type ParamDef,
  type ParamId,
  type ParamValue,
} from '../core/params';
import type { Store } from '../state/store';
import { el, clear } from './dom';
import { infoDot } from './help';
import { icon, type IconName } from './icons';

export interface ExtraSection {
  name: string;
  icon?: IconName;
  node: HTMLElement;
}

export interface ParamEditorOpts {
  store: Store;
  /** Efektivní hodnota k zobrazení. */
  valueOf(id: ParamId): ParamValue;
  /** Je hodnota na editované vrstvě přepsaná (≠ zděděná)? */
  isOverridden(id: ParamId): boolean;
  set(id: ParamId, value: ParamValue): void;
  reset(id: ParamId): void;
  /** Citlivé parametry skrýt (profily je nesmí nést). */
  hideSensitive?: boolean;
  /** Popisek zděděné hodnoty (např. „výchozí" / „zděděno"). */
  inheritedLabel?: string;
  /** Další sekce do navigace (např. „Sdílení a záloha" v Nastavení). */
  extraSections?: ExtraSection[];
}

/** Ikony top-level sekcí (podle názvů kategorií v registru). */
const SECTION_ICONS: Record<string, IconName> = {
  'Doporučení': 'star',
  'Datové zdroje': 'layers',
  'Výpočet': 'gauge',
  'Mapa': 'map',
  'Vzhled': 'palette',
};

const REVEAL_STORAGE = 'optiroute-settings-reveal';

/** Per-(pod)sekce odhalená úroveň komplexnosti (index do PARAM_LEVELS). */
function loadReveal(): Record<string, number> {
  try {
    const raw = localStorage.getItem(REVEAL_STORAGE);
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, unknown>;
      const out: Record<string, number> = {};
      for (const [k, v] of Object.entries(obj)) {
        if (Number.isInteger(v) && (v as number) >= 0 && (v as number) < PARAM_LEVELS.length) {
          out[k] = v as number;
        }
      }
      return out;
    }
  } catch {
    // bez úložiště začínáme na základní úrovni všude
  }
  return {};
}

function saveReveal(reveal: Record<string, number>): void {
  try {
    localStorage.setItem(REVEAL_STORAGE, JSON.stringify(reveal));
  } catch {
    // nevadí
  }
}

/** Naposledy otevřená sekce (drží se po dobu běhu aplikace). */
let lastSection = '';

export function renderParamEditor(opts: ParamEditorOpts): HTMLElement {
  const reveal = loadReveal();
  const tree = buildCategoryTree('dev'); // plný strom; úrovně řeší rozklikávání
  const sections = tree.map((n) => n.name);
  const extras = opts.extraSections ?? [];
  const allSections = [...sections, ...extras.map((e) => e.name)];
  let active = allSections.includes(lastSection) ? lastSection : allSections[0];

  const nav = el('nav', {
    className: 'settings-nav',
    role: 'tablist',
    'aria-label': 'Sekce nastavení',
    'aria-orientation': 'vertical',
  });
  const content = el('div', { className: 'settings-content', role: 'tabpanel' });
  const root = el('div', { className: 'settings-shell' }, nav, content);

  const levelIdxOf = (def: ParamDef): number => PARAM_LEVELS.indexOf(def.level);

  const visibleParams = (node: ParamCategoryNode): ParamId[] =>
    node.params.filter((id) => {
      const def = PARAM_DEFS[id] as ParamDef;
      return !(opts.hideSensitive && def.sensitive);
    });

  /** Klíč (pod)sekce pro ukládání odhalené úrovně. */
  const keyOf = (path: string[]): string => path.join('>');

  const renderNav = (): void => {
    clear(nav);
    for (const name of allSections) {
      const extra = extras.find((e) => e.name === name);
      const iconName = extra?.icon ?? SECTION_ICONS[name] ?? 'settings';
      nav.append(
        el(
          'button',
          {
            className: `settings-nav__item${name === active ? ' settings-nav__item--on' : ''}`,
            type: 'button',
            role: 'tab',
            'aria-selected': String(name === active),
            onclick: () => {
              active = name;
              lastSection = name;
              renderNav();
              renderContent();
              content.scrollTop = 0; // nová sekce → od začátku
            },
          },
          icon(iconName, { size: 15 }),
          el('span', {}, name),
        ),
      );
    }
  };

  /** Překreslení obsahu se zachováním scrollu (žádné skákání nahoru). */
  const renderContent = (preserveScroll = false): void => {
    const scrollKeep = preserveScroll ? content.scrollTop : 0;
    clear(content);
    const extra = extras.find((e) => e.name === active);
    if (extra) {
      content.append(extra.node);
    } else {
      const node = tree.find((n) => n.name === active);
      if (node) content.append(renderGroup(node, 0));
    }
    if (preserveScroll) content.scrollTop = scrollKeep;
  };

  /**
   * Skupina = sekce nebo podsekce. Ukazuje parametry do odhalené úrovně
   * a tlačítko „Zobrazit <další úroveň> (N)" pro postupné rozklikávání.
   */
  const renderGroup = (node: ParamCategoryNode, depth: number): HTMLElement => {
    const key = keyOf(node.path);
    const revealed = reveal[key] ?? 0;
    const params = visibleParams(node);

    const box = el('div', { className: `params-group params-group--d${Math.min(depth, 2)}` });
    if (depth > 0) {
      box.append(el('h4', { className: 'params-group__title' }, node.name));
    }

    const shown = params.filter((id) => levelIdxOf(PARAM_DEFS[id] as ParamDef) <= revealed);
    for (const id of shown) box.append(renderRow(id, PARAM_DEFS[id] as ParamDef));
    if (shown.length === 0 && params.length === 0 && node.children.length === 0) {
      box.append(el('div', { className: 'muted' }, 'Tady zatím nic není.'));
    }

    // Další úroveň k odhalení: nejbližší vyšší úroveň, která má parametry.
    let nextIdx = -1;
    for (let i = revealed + 1; i < PARAM_LEVELS.length; i++) {
      if (params.some((id) => levelIdxOf(PARAM_DEFS[id] as ParamDef) === i)) {
        nextIdx = i;
        break;
      }
    }
    const hiddenCount =
      nextIdx >= 0
        ? params.filter((id) => {
            const li = levelIdxOf(PARAM_DEFS[id] as ParamDef);
            return li > revealed && li <= nextIdx;
          }).length
        : 0;

    const controls = el('div', { className: 'reveal-controls' });
    if (nextIdx >= 0) {
      controls.append(
        el(
          'button',
          {
            className: 'reveal-btn',
            type: 'button',
            onclick: () => {
              reveal[key] = nextIdx;
              saveReveal(reveal);
              renderContent(true);
            },
          },
          icon('chevron-down', { size: 13 }),
          ` Zobrazit ${PARAM_LEVEL_LABELS[PARAM_LEVELS[nextIdx]].toLowerCase()} nastavení (${hiddenCount})`,
        ),
      );
    }
    if (revealed > 0) {
      controls.append(
        el(
          'button',
          {
            className: 'reveal-btn reveal-btn--less',
            type: 'button',
            onclick: () => {
              reveal[key] = 0;
              saveReveal(reveal);
              renderContent(true);
            },
          },
          icon('chevron-up', { size: 13 }),
          ' Skrýt pokročilejší',
        ),
      );
    }
    if (controls.childNodes.length > 0) box.append(controls);

    for (const child of node.children) box.append(renderGroup(child, depth + 1));
    return box;
  };

  const renderRow = (id: ParamId, def: ParamDef): HTMLElement => {
    const overridden = opts.isOverridden(id);
    const value = opts.valueOf(id);

    let input: HTMLElement;
    if (def.type === 'boolean') {
      input = el('input', {
        type: 'checkbox',
        checked: value === true,
        'aria-label': def.label,
        onchange: (e: Event) => {
          opts.set(id, (e.target as HTMLInputElement).checked);
          renderContent(true);
        },
      });
    } else if (def.type === 'number') {
      input = el('input', {
        className: 'input input--small',
        type: 'number',
        value: String(value),
        min: def.min !== undefined ? String(def.min) : undefined,
        max: def.max !== undefined ? String(def.max) : undefined,
        step: def.step !== undefined ? String(def.step) : undefined,
        'aria-label': def.label,
        onchange: (e: Event) => {
          const n = Number((e.target as HTMLInputElement).value.replace(',', '.'));
          if (Number.isFinite(n)) {
            opts.set(id, n);
            renderContent(true);
          }
        },
      });
    } else if (def.type === 'select') {
      input = el(
        'select',
        {
          className: 'select select--small',
          'aria-label': def.label,
          onchange: (e: Event) => {
            opts.set(id, (e.target as HTMLSelectElement).value);
            renderContent(true);
          },
        },
        ...(def.options ?? []).map((o) =>
          el('option', { value: o.value, selected: o.value === value }, o.label),
        ),
      );
    } else {
      input = el('input', {
        className: 'input input--small',
        type: 'text',
        value: String(value ?? ''),
        placeholder: def.sensitive ? 'jen v tomto prohlížeči' : '',
        'aria-label': def.label,
        onchange: (e: Event) => {
          opts.set(id, (e.target as HTMLInputElement).value.trim());
          renderContent(true);
        },
      });
    }

    return el(
      'div',
      { className: 'param-row' },
      el(
        'div',
        { className: 'param-row__label' },
        el('span', {}, def.label),
        def.help ? infoDot(opts.store, def.help) : null,
        def.sensitive
          ? el('span', { className: 'param-badge param-badge--local', title: 'Ukládá se jen v tomto prohlížeči' }, 'lokální')
          : null,
      ),
      el(
        'div',
        { className: 'param-row__value' },
        input,
        def.unit && def.type === 'number' ? el('span', { className: 'muted' }, def.unit) : null,
        overridden
          ? el(
              'button',
              {
                className: 'btn btn--ghost btn--small',
                title: `Vrátit na: ${opts.inheritedLabel ?? 'výchozí'}`,
                'aria-label': `Vrátit ${def.label} na zděděnou hodnotu`,
                onclick: () => {
                  opts.reset(id);
                  renderContent(true);
                },
              },
              '↺',
            )
          : el('span', { className: 'param-badge' }, opts.inheritedLabel ?? 'výchozí'),
      ),
    );
  };

  renderNav();
  renderContent();
  return root;
}
